/**
 * Leaderboard & Siegbedingungen
 * Liest alle Daten aus fixture.leaderboard — kein Hardcode.
 */
import { state, formatCash } from '../main';
import { fixture } from '../data/fixture';
import { t } from '../i18n';
import { openCorpDossier } from './corp-dossier';

export function renderLeaderboard(): HTMLElement {
  const standings = fixture.leaderboard ?? [];
  const totalSectors = fixture.sectors?.length ?? 0;
  const cycleMax    = fixture.cycleMax ?? state.cycleMax;
  const cycle       = state.cycle;

  // Derive facts for VC cards
  const me = standings.find(c => c.isMe);
  const rank1 = standings[0];

  const myTerritory  = me?.territory  ?? 0;
  const myNetWorth   = me?.netWorth   ?? 0;
  const myRank       = me?.rank       ?? standings.length;

  const dominanceThresholdPct = fixture.dominanceThresholdPct ?? 70;
  const territoryLeaderPct    = rank1 ? Math.round((rank1.territory / Math.max(totalSectors, 1)) * 100) : 0;
  const myTerritoryPct        = totalSectors > 0 ? Math.round((myTerritory / totalSectors) * 100) : 0;
  const eliminatedCount       = standings.filter(c => c.eliminated).length;
  const totalOpponents        = Math.max(standings.length - 1, 0);

  const VICTORY_CONDITIONS = [
    {
      id: 'elimination',
      title: t('leaderboard.vc_elimination_title'),
      desc: t('leaderboard.vc_elimination_desc'),
      priority: 1,
      leader: { name: eliminatedCount > 0 ? (rank1?.name ?? '—') : '—', value: t('leaderboard.vc_eliminated_count', { count: String(eliminatedCount) }) },
      mine: t('leaderboard.vc_eliminated_progress', { count: String(eliminatedCount), total: String(totalOpponents) }),
      pct: totalOpponents > 0 ? Math.round((eliminatedCount / totalOpponents) * 100) : 0,
      color: 'var(--red)',
    },
    {
      id: 'territory',
      title: t('leaderboard.vc_territory_title'),
      desc: t('leaderboard.vc_territory_desc', { pct: String(dominanceThresholdPct) }),
      priority: 2,
      leader: { name: rank1?.name ?? '—', value: `${territoryLeaderPct}%` },
      mine: `${myTerritoryPct}% / ${dominanceThresholdPct}%`,
      pct: Math.min(Math.round((myTerritoryPct / dominanceThresholdPct) * 100), 100),
      color: 'var(--amber)',
    },
    {
      id: 'networth',
      title: t('leaderboard.vc_networth_title'),
      desc: t('leaderboard.vc_networth_desc'),
      priority: 3,
      leader: { name: rank1?.name ?? '—', value: formatCash(rank1?.netWorth ?? 0) },
      mine: t('leaderboard.vc_networth_mine', { value: formatCash(myNetWorth), rank: String(myRank) }),
      pct: rank1 && rank1.netWorth > 0 ? Math.min(Math.round((myNetWorth / rank1.netWorth) * 100), 100) : 0,
      color: 'var(--cyan)',
    },
    {
      id: 'politics',
      title: t('leaderboard.vc_politics_title'),
      desc: t('leaderboard.vc_politics_desc'),
      priority: 4,
      leader: { name: rank1?.name ?? '—', value: t('leaderboard.vc_sectors', { count: String(rank1?.territory ?? 0) }) },
      mine: t('leaderboard.vc_sectors_rank', { count: String(myTerritory), rank: String(myRank) }),
      pct: rank1 && rank1.territory > 0
        ? Math.min(Math.round((myTerritory / rank1.territory) * 100), 100)
        : 0,
      color: 'var(--magenta)',
    },
  ];

  const root = document.createElement('div');
  root.className = 'screen';

  const style = document.createElement('style');
  style.textContent = `
    /* .lb-table → shared .dtable (marker kept); only the me-row highlight stays local */
    .lb-table .me-row td { background:rgba(var(--ac),0.04); }
    .lb-table .me-row:hover td { background:rgba(var(--ac),0.08); }
    .vc-card { background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); border:1px solid var(--line); border-radius:4px; padding:14px; border-left:3px solid transparent; }
    .vc-prio { font-family:var(--mono); font-size:11px; letter-spacing:2px; text-transform:uppercase; color:var(--text-lo); margin-bottom:4px; }
    .vc-title { font-size:15px; font-weight:700; color:var(--text-hi); margin-bottom:4px; }
    .vc-desc { font-size:12px; color:var(--text-lo); margin-bottom:10px; }
    .vc-bar { height:6px; background:var(--border); border-radius:3px; overflow:hidden; margin:6px 0; }
    .vc-bar-fill { height:100%; border-radius:3px; }
    .lb-elim { font-size:11px; font-family:var(--mono); color:var(--red); }
  `;
  root.appendChild(style);

  // Generate stable hue per standing rank (deterministic, no RNG)
  const COLORS = ['#e74c3c','#9b59b6','#3498db','#e67e22','#2ecc71','#f39c12','#1abc9c','#e91e63'];

  const vcCards = VICTORY_CONDITIONS.map(vc => `
    <div class="vc-card" style="border-left-color:${vc.color}">
      <div class="vc-prio">${t('leaderboard.vc_priority', { n: String(vc.priority) })}</div>
      <div class="vc-title">${vc.title}</div>
      <div class="vc-desc">${vc.desc}</div>
      <div style="display:flex; justify-content:space-between; font-size:12px; color:var(--text-lo)">
        <span>${t('leaderboard.vc_leader')}<span class="corp-link" data-corp="${vc.leader.name}" style="color:var(--text-mid);cursor:pointer">${vc.leader.name} (${vc.leader.value})</span></span>
        <span>${t('leaderboard.vc_mine')}<span style="color:${vc.color}">${vc.mine}</span></span>
      </div>
      <div class="vc-bar">
        <div class="vc-bar-fill" style="width:${vc.pct}%; background:${vc.color}"></div>
      </div>
    </div>
  `).join('');

  const lbRows = standings.map((c, i) => {
    const color = c.isMe ? 'var(--cyan)' : (c.eliminated ? 'rgba(230,57,70,0.6)' : COLORS[i % COLORS.length]);
    const territoryPct = totalSectors > 0 ? Math.round((c.territory / totalSectors) * 100) : c.territory;
    return `<tr class="${c.isMe ? 'me-row' : ''}">
      <td style="font-family:var(--mono); font-size:14px; color:${c.isMe ? 'var(--cyan)' : 'var(--text-lo)'}; font-weight:600">#${c.rank}</td>
      <td>
        <div style="display:flex; align-items:center; gap:8px">
          <div style="width:10px; height:10px; border-radius:50%; background:${color}; flex-shrink:0"></div>
          <span class="corp-link" data-corp="${c.name}" style="font-weight:600; cursor:pointer; color:${c.isMe ? 'var(--cyan)' : (c.eliminated ? 'rgba(255,255,255,0.3)' : 'var(--text-hi)')}"
                ${c.eliminated ? 'style="text-decoration:line-through;color:rgba(255,255,255,0.3)"' : ''}>
            ${c.isMe ? `&#9658; ${c.name} &#9668;` : c.name}
          </span>
        </div>
      </td>
      <td style="font-family:var(--mono); color:${c.eliminated ? 'rgba(255,255,255,0.2)' : 'var(--amber)'}">
        ${c.eliminated ? `<span style="color:var(--red);font-size:11px">${t('leaderboard.eliminated')}</span>` : formatCash(c.netWorth)}
      </td>
      <td>
        <div style="display:flex; align-items:center; gap:6px">
          <div style="width:80px; height:4px; background:var(--border); border-radius:2px; overflow:hidden">
            <div style="height:100%; width:${Math.min(territoryPct, 100)}%; background:${color}; border-radius:2px"></div>
          </div>
          <span style="font-family:var(--mono); font-size:12px; color:var(--text-mid)">${territoryPct}%</span>
        </div>
      </td>
      <td style="text-align:center; font-size:11px; font-family:var(--mono); color:${c.eliminated ? 'var(--red)' : 'var(--text-lo)'}">
        ${c.eliminated ? 'out' : (c.rank === 1 ? `<span style="color:var(--amber)">${t('leaderboard.leading')}</span>` : '—')}
      </td>
    </tr>`;
  }).join('');

  const playerCount = standings.filter(c => !c.eliminated).length;
  const modeLabel = cycleMax <= 20 ? t('leaderboard.mode_quick') : cycleMax <= 30 ? t('leaderboard.mode_standard') : t('leaderboard.mode_epic');

  root.innerHTML += `
    <div class="screen-title">${t('leaderboard.title', { cycle: String(cycle), max: String(cycleMax) })}</div>

    <div style="margin-bottom:24px">
      <div class="panel-title" style="margin-bottom:12px; font-family:var(--mono); font-size:12px; letter-spacing:2px; text-transform:uppercase; color:var(--text-lo)">${t('leaderboard.victory_conditions')}</div>
      <div style="display:grid; grid-template-columns:repeat(2, 1fr); gap:12px; margin-bottom:20px">${vcCards}</div>
    </div>

    <div class="panel">
      <div class="panel-title">${t('leaderboard.panel_ranking')}</div>
      <table class="lb-table dtable">
        <thead><tr>
          <th>${t('leaderboard.col_rank')}</th><th>${t('leaderboard.col_corp')}</th><th>${t('leaderboard.col_networth')}</th><th>${t('leaderboard.col_territory')}</th><th>${t('leaderboard.col_status')}</th>
        </tr></thead>
        <tbody>${lbRows}</tbody>
      </table>
    </div>

    <div style="margin-top:12px; padding:10px 16px; background:var(--bg-panel); border:1px solid var(--border); border-radius:4px; font-size:12px; color:var(--text-lo); font-family:var(--mono)">
      ${t('leaderboard.footer', { mode: modeLabel, cycleMax: String(cycleMax), sectors: String(totalSectors), players: String(playerCount), antiDump: String(cycleMax - 5) })}
    </div>
  `;

  root.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest('.corp-link') as HTMLElement | null;
    if (link?.dataset.corp) openCorpDossier(link.dataset.corp);
  });

  return root;
}
